import { useEffect, useState, type DependencyList } from 'react'
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, where, type Unsubscribe } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import { callable } from './callable'

export type ReliabilityStats = {
  successfulScans: number
  completedChecklistItems: number
  completedTasks: number
  blockerEvents: number
  vouches: number
  ratio: number
  windowDays: number
  calculatedAt: string
}

export type ReliabilityLeaderboardRow = {
  uid: string
  role: string
  displayName: string
  reliabilityScore: number
  reliabilityStats: ReliabilityStats | null
  updatedAt: string | null
}

export type ReliabilitySnapshot = {
  uid: string
  workspaceId: string
  reliabilityScore: number | null
  reliabilityStats: ReliabilityStats | null
  updatedAt: string | null
}

export type WorkspaceSocialEvent = {
  id: string
  workspaceId: string
  type: 'RAID_STARTED' | 'RAID_COMPLETED' | 'VOUCH_CREATED'
  title: string
  summary: string
  targetId: string
  targetType: string
  participantUids?: string[]
  createdAt: unknown
}

export type ReliableCollectionStatus = 'idle' | 'loading' | 'ready' | 'degraded' | 'error'

export type ReliableCollectionState<T> = {
  status: ReliableCollectionStatus
  data: T[]
  error: Error | null
}

const CIRCUIT_FAILURE_LIMIT = 3
const CIRCUIT_WINDOW_MS = 60_000
const CIRCUIT_RETRY_MS = 5 * 60_000

const recentFailuresBySource = new Map<string, number[]>()

export const recordCircuitBreakerFailure = (source: string, now = Date.now()): boolean => {
  const recent = (recentFailuresBySource.get(source) ?? []).filter((at) => now - at <= CIRCUIT_WINDOW_MS)
  recent.push(now)
  recentFailuresBySource.set(source, recent)
  return recent.length >= CIRCUIT_FAILURE_LIMIT
}

export const resetCircuitBreakerFailures = (source: string): void => {
  recentFailuresBySource.delete(source)
}

const subscribeWithCircuitBreaker = (
  source: string,
  start: (onError: (error: Error) => void) => Unsubscribe,
  onError?: (error: Error) => void,
): Unsubscribe => {
  let closed = false
  let warned = false
  let unsubscribe: Unsubscribe | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const open = () => {
    if (closed) return
    unsubscribe = start((error) => {
      onError?.(error)
      if (!recordCircuitBreakerFailure(source)) return
      unsubscribe?.()
      unsubscribe = null
      if (!warned) {
        warned = true
        console.warn(`[reliabilityService] ${source} listener degraded; retrying after cooldown.`, error)
      }
      retryTimer = setTimeout(() => {
        retryTimer = null
        resetCircuitBreakerFailures(source)
        warned = false
        open()
      }, CIRCUIT_RETRY_MS)
    })
  }

  open()

  return () => {
    closed = true
    unsubscribe?.()
    if (retryTimer !== null) clearTimeout(retryTimer)
  }
}

export const useReliableCollection = <T,>(
  source: string,
  subscribe: (onChange: (items: T[]) => void, onError: (error: Error) => void) => Unsubscribe,
  deps: DependencyList,
): ReliableCollectionState<T> => {
  const [prevDeps, setPrevDeps] = useState(deps)
  const [state, setState] = useState<ReliableCollectionState<T>>({ status: 'loading', data: [], error: null })

  const depsChanged =
    deps.length !== prevDeps.length || deps.some((dep, idx) => !Object.is(dep, prevDeps[idx]))

  if (depsChanged) {
    setPrevDeps(deps)
    setState((current) => ({ ...current, status: 'loading', error: null }))
  }

  useEffect(() => {
    const stop = subscribe(
      (data) => setState({ status: 'ready', data, error: null }),
      (error) => setState((current) => ({
        status: recordCircuitBreakerFailure(source) ? 'degraded' : 'error',
        data: current.data,
        error,
      })),
    )
    return () => stop()
  }, deps)
  return state
}

export const getReliabilityLeaderboard = async (workspaceId: string) => {
  return callable<{ workspaceId: string }, { workspaceId: string; rows: ReliabilityLeaderboardRow[] }>('getReliabilityLeaderboard', { workspaceId })
}

export const vouchForChecklistCompletion = async (checklistItemId: string) => {
  const callable = httpsCallable<{ checklistItemId: string }, { uid: string; workspaceId: string; checklistItemId: string; reliabilityScore: number; reliabilityStats: ReliabilityStats }>(firebaseFunctions, 'vouchForChecklistCompletion')
  return (await callable({ checklistItemId })).data
}

const numberOrZero = (value: unknown): number => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

const timestampToIso = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === 'string') return value
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate().toISOString()
  }
  return null
}

export const normalizeReliabilityStats = (value: unknown): ReliabilityStats | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const data = value as Record<string, unknown>
  return {
    successfulScans: numberOrZero(data.successfulScans),
    completedChecklistItems: numberOrZero(data.completedChecklistItems),
    completedTasks: numberOrZero(data.completedTasks),
    blockerEvents: numberOrZero(data.blockerEvents),
    vouches: numberOrZero(data.vouches),
    ratio: numberOrZero(data.ratio),
    windowDays: numberOrZero(data.windowDays) || 14,
    calculatedAt: typeof data.calculatedAt === 'string' ? data.calculatedAt : '',
  }
}

export const normalizeReliabilitySnapshot = (
  uid: string,
  workspaceId: string,
  data: Record<string, unknown> | null | undefined,
): ReliabilitySnapshot => ({
  uid,
  workspaceId,
  reliabilityScore: typeof data?.reliabilityScore === 'number' && Number.isFinite(data.reliabilityScore)
    ? data.reliabilityScore
    : null,
  reliabilityStats: normalizeReliabilityStats(data?.reliabilityStats),
  updatedAt: timestampToIso(data?.reliabilityUpdatedAt),
})

export const subscribeReliabilityStats = (
  workspaceId: string,
  uid: string | null | undefined,
  onChange: (snapshot: ReliabilitySnapshot) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  if (!workspaceId || !uid) {
    onChange(normalizeReliabilitySnapshot(uid ?? '', workspaceId, null))
    return () => {}
  }
  return onSnapshot(
    doc(firestoreDb, 'users', uid, 'workspaceMemberships', workspaceId),
    (snapshot) => onChange(normalizeReliabilitySnapshot(uid, workspaceId, snapshot.exists() ? snapshot.data() : null)),
    (error) => onError?.(error),
  )
}

export const getRaidsCompletedCount = async (workspaceId: string, uid: string) => {
  const snapshot = await getDocs(query(
    collection(firestoreDb, 'workspace_social_events'),
    where('workspaceId', '==', workspaceId),
    where('type', '==', 'RAID_COMPLETED'),
    where('participantUids', 'array-contains', uid),
  ))
  return snapshot.size
}

export const subscribeWorkspaceSocialEvents = (
  workspaceId: string,
  onChange: (events: WorkspaceSocialEvent[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => subscribeWithCircuitBreaker(
  `workspace_social_events:${workspaceId}`,
  (handleError) => onSnapshot(
    query(collection(firestoreDb, 'workspace_social_events'), where('workspaceId', '==', workspaceId), orderBy('createdAt', 'desc'), limit(20)),
    (snapshot) => {
      resetCircuitBreakerFailures(`workspace_social_events:${workspaceId}`)
      onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as WorkspaceSocialEvent))
    },
    (error) => handleError(error),
  ),
  onError,
)

export const subscribeHelperVouchEvents = (
  workspaceId: string,
  uid: string | null | undefined,
  onChange: (events: WorkspaceSocialEvent[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  if (!workspaceId || !uid) {
    onChange([])
    return () => {}
  }
  return subscribeWithCircuitBreaker(
    `workspace_social_events:vouches:${workspaceId}:${uid}`,
    (handleError) => onSnapshot(
      query(
        collection(firestoreDb, 'workspace_social_events'),
        where('workspaceId', '==', workspaceId),
        where('type', '==', 'VOUCH_CREATED'),
        where('participantUids', 'array-contains', uid),
        limit(20),
      ),
      (snapshot) => {
        resetCircuitBreakerFailures(`workspace_social_events:vouches:${workspaceId}:${uid}`)
        onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as WorkspaceSocialEvent))
      },
      (error) => handleError(error),
    ),
    onError,
  )
}
